import { useState, useRef, useCallback, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FileText,
  Loader2,
  Trash2,
  Info,
  RefreshCw,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Upload,
  X,
  Copy,
  Check,
  Eye,
  Code,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';

const PROXY_BASE_URL = 'https://proxy-instance.float16.cloud';
const DEFAULT_VLLM_PORT = 3900;

// Fixed prompt for Typhoon OCR - task-specific model
const TYPHOON_OCR_PROMPT = `Extract all text from the image.

Instructions:
- Only return the clean Markdown.
- Do not include any explanation or extra text.
- You must include all information on the page.

Formatting Rules:
- Tables: Render tables using <table>...</table> in clean HTML format.
- Equations: Render equations using LaTeX syntax with inline ($...$) and block ($$...$$).
- Images/Charts/Diagrams: Wrap in <figure>...</figure> with descriptions in Thai.
- Page Numbers: Wrap in <page_number>...</page_number>.
- Checkboxes: Use ☐ for unchecked and ☑ for checked boxes.`;

interface VLLMModel {
  id: string;
  object: string;
  created: number;
  owned_by: string;
}

type HealthStatus = 'checking' | 'healthy' | 'unhealthy' | 'unknown';

interface TyphoonOCRPlaygroundProps {
  parentTaskId: string;
}

const TyphoonOCRPlayground = ({ parentTaskId }: TyphoonOCRPlaygroundProps) => {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadedFileName, setUploadedFileName] = useState<string>('');
  const imageInputRef = useRef<HTMLInputElement>(null);

  const [isProcessing, setIsProcessing] = useState(false);
  const [ocrResult, setOcrResult] = useState<string>('');
  const [resultViewMode, setResultViewMode] = useState<'preview' | 'markdown'>('preview');
  const [copied, setCopied] = useState(false);

  const [port, setPort] = useState(DEFAULT_VLLM_PORT);
  const [selectedModel, setSelectedModel] = useState('');
  const [availableModels, setAvailableModels] = useState<VLLMModel[]>([]);
  const [healthStatus, setHealthStatus] = useState<HealthStatus>('unknown');
  const [isLoadingModels, setIsLoadingModels] = useState(false);

  // Code dialog state
  const [showCodeDialog, setShowCodeDialog] = useState(false);
  const [codeCopied, setCodeCopied] = useState(false);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Build proxy endpoint
  const proxyBase = `${PROXY_BASE_URL}/${parentTaskId}/${port}`;
  const chatCompletionsEndpoint = `${proxyBase}/v1/chat/completions`;
  const healthEndpoint = `${proxyBase}/health`;
  const modelsEndpoint = `${proxyBase}/v1/models`;

  // Check health status
  const checkHealth = useCallback(async () => {
    if (!parentTaskId) return;

    setHealthStatus('checking');
    try {
      const response = await fetch(healthEndpoint, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      setHealthStatus(response.ok ? 'healthy' : 'unhealthy');
    } catch {
      setHealthStatus('unhealthy');
    }
  }, [healthEndpoint, parentTaskId]);

  // Fetch available models
  const fetchModels = useCallback(async () => {
    if (!parentTaskId) return;

    setIsLoadingModels(true);
    try {
      const response = await fetch(modelsEndpoint, {
        method: 'GET',
        signal: AbortSignal.timeout(5000),
      });
      if (response.ok) {
        const data = await response.json();
        const models: VLLMModel[] = data.data || [];
        setAvailableModels(models);
        if (models.length > 0 && !selectedModel) {
          setSelectedModel(models[0].id);
        }
      }
    } catch {
      setAvailableModels([]);
    } finally {
      setIsLoadingModels(false);
    }
  }, [modelsEndpoint, parentTaskId, selectedModel]);

  // Refresh both health and models
  const refreshStatus = useCallback(async () => {
    await Promise.all([checkHealth(), fetchModels()]);
  }, [checkHealth, fetchModels]);

  // Check health and fetch models on mount and when port changes
  useEffect(() => {
    if (parentTaskId) {
      refreshStatus();
    }
  }, [parentTaskId, port, refreshStatus]);

  // Build request body for code examples
  const buildExampleRequestBody = useCallback(() => {
    return {
      model: selectedModel || 'typhoon-ocr',
      messages: [
        {
          role: 'user',
          content: [
            { type: 'text', text: TYPHOON_OCR_PROMPT },
            {
              type: 'image_url',
              image_url: { url: 'data:image/png;base64,<YOUR_BASE64_IMAGE>' },
            },
          ],
        },
      ],
      max_tokens: 10000,
      temperature: 0,
      stream: true,
    };
  }, [selectedModel]);

  // Generate Python code example
  const generatePythonCode = useCallback(() => {
    const baseUrl = chatCompletionsEndpoint.replace('/v1/chat/completions', '/v1');

    return `from openai import OpenAI
import base64

client = OpenAI(
    base_url="${baseUrl}",
    api_key="not-needed"  # vLLM doesn't require API key
)

# Read and encode your image
def encode_image(image_path: str) -> str:
    with open(image_path, "rb") as f:
        return base64.b64encode(f.read()).decode("utf-8")

image_base64 = encode_image("your_document.png")

# Typhoon OCR prompt (required - task-specific model)
prompt = """${TYPHOON_OCR_PROMPT.replace(/\n/g, '\\n')}"""

response = client.chat.completions.create(
    model="${selectedModel || 'typhoon-ocr'}",
    messages=[
        {
            "role": "user",
            "content": [
                {"type": "text", "text": prompt},
                {
                    "type": "image_url",
                    "image_url": {"url": f"data:image/png;base64,{image_base64}"}
                }
            ]
        }
    ],
    max_tokens=10000,
    temperature=0,
    stream=True
)

# Stream the OCR result
for chunk in response:
    if chunk.choices[0].delta.content:
        print(chunk.choices[0].delta.content, end="", flush=True)
`;
  }, [chatCompletionsEndpoint, selectedModel]);

  // Generate curl command
  const generateCurlCommand = useCallback(() => {
    const requestBody = buildExampleRequestBody();

    return `# First, encode your image to base64:
# base64 -i your_document.png -o image_base64.txt

curl -X POST "${chatCompletionsEndpoint}" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(requestBody, null, 2).replace(/'/g, "'\\''")}'`;
  }, [buildExampleRequestBody, chatCompletionsEndpoint]);

  // Copy code to clipboard
  const handleCopyCode = useCallback(async (code: string) => {
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }, []);

  // Handle image upload
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      return;
    }

    setUploadedFileName(file.name);

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64 = event.target?.result as string;
      setUploadedImage(base64);
      setOcrResult(''); // Clear previous result
    };
    reader.readAsDataURL(file);

    // Reset input
    if (imageInputRef.current) {
      imageInputRef.current.value = '';
    }
  };

  const clearImage = () => {
    setUploadedImage(null);
    setUploadedFileName('');
    setOcrResult('');
  };

  const handleStop = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  };

  const handleExtractText = async () => {
    if (!uploadedImage || isProcessing || !parentTaskId) return;

    setIsProcessing(true);
    setOcrResult('');

    abortControllerRef.current = new AbortController();

    try {
      const response = await fetch(chatCompletionsEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...(selectedModel && { model: selectedModel }),
          messages: [
            {
              role: 'user',
              content: [
                { type: 'text', text: TYPHOON_OCR_PROMPT },
                {
                  type: 'image_url',
                  image_url: { url: uploadedImage },
                },
              ],
            },
          ],
          max_tokens: 10000, // Typhoon OCR needs high token limit
          temperature: 0, // Deterministic output for OCR
          stream: true,
        }),
        signal: abortControllerRef.current.signal,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${await response.text()}`);
      }

      if (response.body) {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            const trimmedLine = line.trim();
            if (!trimmedLine) continue;

            if (trimmedLine.startsWith('data: ')) {
              const data = trimmedLine.slice(6);
              if (data === '[DONE]') continue;

              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content || '';
                if (content) {
                  fullContent += content;
                  setOcrResult(fullContent);
                }
              } catch {
                // Skip invalid JSON
              }
            }
          }
        }

        // Process remaining buffer
        if (buffer.trim()) {
          const trimmedLine = buffer.trim();
          if (trimmedLine.startsWith('data: ')) {
            const data = trimmedLine.slice(6);
            if (data !== '[DONE]') {
              try {
                const parsed = JSON.parse(data);
                const content = parsed.choices?.[0]?.delta?.content || '';
                if (content) {
                  fullContent += content;
                  setOcrResult(fullContent);
                }
              } catch {
                // Skip invalid JSON
              }
            }
          }
        }
      }
    } catch (error: any) {
      if (error.name === 'AbortError') {
        setOcrResult((prev) => prev + '\n\n[Extraction stopped]');
      } else {
        setOcrResult(`Error: ${error.message}`);
      }
    } finally {
      setIsProcessing(false);
      abortControllerRef.current = null;
    }
  };

  const handleCopyResult = async () => {
    if (!ocrResult) return;
    try {
      await navigator.clipboard.writeText(ocrResult);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="container py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <FileText className="h-6 w-6 text-pink-500" />
            Typhoon OCR Playground
          </h1>
          <p className="text-muted-foreground mt-1">
            Extract text from documents with Thai and English support
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-pink-600 border-pink-500/30">
            <FileText className="h-3 w-3 mr-1" />
            OCR
          </Badge>
          {selectedModel && (
            <Badge variant="secondary" className="font-mono text-xs">
              {selectedModel}
            </Badge>
          )}
          <Badge
            variant="outline"
            className={
              healthStatus === 'healthy'
                ? 'text-green-600 border-green-500/30'
                : healthStatus === 'unhealthy'
                ? 'text-red-600 border-red-500/30'
                : 'text-muted-foreground'
            }
          >
            {healthStatus === 'checking' && <Loader2 className="h-3 w-3 animate-spin mr-1" />}
            {healthStatus === 'healthy' && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {healthStatus === 'unhealthy' && <XCircle className="h-3 w-3 mr-1" />}
            {healthStatus === 'checking' ? 'Checking' : healthStatus}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-4">
        {/* Settings Panel */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Settings</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Health Status */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Server Status</Label>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={refreshStatus}
                  disabled={healthStatus === 'checking' || isLoadingModels}
                  className="h-7 px-2"
                >
                  <RefreshCw className={`h-3 w-3 ${healthStatus === 'checking' ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                {healthStatus === 'checking' && (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Checking...</span>
                  </>
                )}
                {healthStatus === 'healthy' && (
                  <>
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    <span className="text-sm text-green-600">Healthy</span>
                  </>
                )}
                {healthStatus === 'unhealthy' && (
                  <>
                    <XCircle className="h-4 w-4 text-red-500" />
                    <span className="text-sm text-red-600">Unhealthy</span>
                  </>
                )}
                {healthStatus === 'unknown' && (
                  <>
                    <AlertCircle className="h-4 w-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Unknown</span>
                  </>
                )}
              </div>
            </div>

            {/* Port Settings */}
            <div className="space-y-3">
              <Label>Port (3000-4000)</Label>
              <Input
                type="number"
                min={3000}
                max={4000}
                value={port}
                onChange={(e) => {
                  const p = parseInt(e.target.value);
                  if (p >= 3000 && p <= 4000) setPort(p);
                }}
                disabled={isProcessing}
                className="font-mono"
              />
            </div>

            {/* Model Selector */}
            <div className="space-y-3">
              <Label>Model</Label>
              {isLoadingModels ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading models...
                </div>
              ) : availableModels.length > 0 ? (
                <Select value={selectedModel} onValueChange={setSelectedModel} disabled={isProcessing}>
                  <SelectTrigger className="font-mono text-xs">
                    <SelectValue placeholder="Select model" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableModels.map((model) => (
                      <SelectItem key={model.id} value={model.id} className="font-mono text-xs">
                        {model.id}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No models available
                </div>
              )}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={clearImage}
              disabled={isProcessing || !uploadedImage}
              className="w-full"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </CardContent>
        </Card>

        {/* Main Panel */}
        <div className="lg:col-span-3 space-y-6">
          {/* Upload Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-base">Document Upload</CardTitle>
                  <CardDescription>
                    Upload an image or PDF document to extract text
                  </CardDescription>
                </div>
                <Dialog open={showCodeDialog} onOpenChange={setShowCodeDialog}>
                  <DialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Code className="h-4 w-4 mr-2" />
                      View Code
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                      <DialogTitle>Typhoon OCR API Example</DialogTitle>
                      <DialogDescription>
                        Copy the code below to integrate OCR in your application
                      </DialogDescription>
                    </DialogHeader>
                    <Tabs defaultValue="python" className="flex-1 overflow-hidden flex flex-col">
                      <TabsList className="w-full justify-start">
                        <TabsTrigger value="python">Python</TabsTrigger>
                        <TabsTrigger value="curl">cURL</TabsTrigger>
                        <TabsTrigger value="json">Request Body</TabsTrigger>
                      </TabsList>
                      <TabsContent value="python" className="flex-1 overflow-hidden">
                        <div className="relative h-full">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2 z-10"
                            onClick={() => handleCopyCode(generatePythonCode())}
                          >
                            {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <pre className="h-[400px] overflow-auto bg-muted p-4 rounded-lg text-xs font-mono">
                            {generatePythonCode()}
                          </pre>
                        </div>
                      </TabsContent>
                      <TabsContent value="curl" className="flex-1 overflow-hidden">
                        <div className="relative h-full">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2 z-10"
                            onClick={() => handleCopyCode(generateCurlCommand())}
                          >
                            {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <pre className="h-[400px] overflow-auto bg-muted p-4 rounded-lg text-xs font-mono whitespace-pre-wrap">
                            {generateCurlCommand()}
                          </pre>
                        </div>
                      </TabsContent>
                      <TabsContent value="json" className="flex-1 overflow-hidden">
                        <div className="relative h-full">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="absolute top-2 right-2 z-10"
                            onClick={() => handleCopyCode(JSON.stringify(buildExampleRequestBody(), null, 2))}
                          >
                            {codeCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                          </Button>
                          <pre className="h-[400px] overflow-auto bg-muted p-4 rounded-lg text-xs font-mono">
                            {JSON.stringify(buildExampleRequestBody(), null, 2)}
                          </pre>
                        </div>
                      </TabsContent>
                    </Tabs>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              {!uploadedImage ? (
                <div
                  onClick={() => imageInputRef.current?.click()}
                  className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
                >
                  <input
                    ref={imageInputRef}
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Upload className="h-10 w-10 mx-auto text-muted-foreground mb-4" />
                  <p className="text-sm font-medium">Click to upload</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    PNG, JPG, or PDF (max 1800px dimension recommended)
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="relative flex-shrink-0">
                      <img
                        src={uploadedImage}
                        alt="Uploaded document"
                        className="max-w-[300px] max-h-[400px] rounded-lg border object-contain"
                      />
                      <button
                        onClick={clearImage}
                        className="absolute -top-2 -right-2 bg-destructive text-destructive-foreground rounded-full p-1 hover:bg-destructive/90"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                    <div className="flex-1 space-y-3">
                      <div>
                        <p className="text-sm font-medium">{uploadedFileName}</p>
                        <p className="text-xs text-muted-foreground">Ready for extraction</p>
                      </div>
                      <Button
                        onClick={handleExtractText}
                        disabled={isProcessing || healthStatus !== 'healthy'}
                        className="w-full"
                      >
                        {isProcessing ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                            Extracting...
                          </>
                        ) : (
                          <>
                            <FileText className="h-4 w-4 mr-2" />
                            Extract Text
                          </>
                        )}
                      </Button>
                      {isProcessing && (
                        <Button variant="destructive" onClick={handleStop} className="w-full">
                          Stop Extraction
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Result Section */}
          {(ocrResult || isProcessing) && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base">Extracted Text</CardTitle>
                  <div className="flex items-center gap-2">
                    <Tabs value={resultViewMode} onValueChange={(v) => setResultViewMode(v as 'preview' | 'markdown')}>
                      <TabsList className="h-8">
                        <TabsTrigger value="preview" className="text-xs h-7 px-2">
                          <Eye className="h-3 w-3 mr-1" />
                          Preview
                        </TabsTrigger>
                        <TabsTrigger value="markdown" className="text-xs h-7 px-2">
                          <Code className="h-3 w-3 mr-1" />
                          Markdown
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleCopyResult}
                      disabled={!ocrResult}
                      className="h-8"
                    >
                      {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="border rounded-lg p-4 bg-muted/30 min-h-[300px] max-h-[600px] overflow-y-auto">
                  {resultViewMode === 'preview' ? (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {ocrResult || 'Processing...'}
                      </ReactMarkdown>
                      {isProcessing && (
                        <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1" />
                      )}
                    </div>
                  ) : (
                    <pre className="text-sm whitespace-pre-wrap font-mono">
                      {ocrResult || 'Processing...'}
                      {isProcessing && (
                        <span className="inline-block w-2 h-4 bg-primary animate-pulse ml-1" />
                      )}
                    </pre>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Info */}
      <Alert className="mt-6">
        <Info className="h-4 w-4" />
        <AlertDescription>
          <div className="space-y-1">
            <p><strong>Typhoon OCR 1.5</strong> - Vision-language OCR model for Thai and English documents</p>
            <p className="text-sm text-muted-foreground">
              Outputs structured markdown with HTML tables, LaTeX equations, and figure descriptions.
              Trained with 1800px dimension images.
            </p>
          </div>
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default TyphoonOCRPlayground;
